import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Building2, Plus, Trash2, Copy, ExternalLink, UserPlus, KeyRound, CheckCircle2, Clock, Eye, LogIn, Link as LinkIcon, FileText, TrendingUp, Package, Send, Mail } from "lucide-react";

const emptyForm = {
  nom: "", code: "", contact_nom: "", contact_email: "", contact_telephone: "",
  adresse: "", ville: "", code_postal: "", remise_pct: 0,
};

const B2BAdmin = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [entreprises, setEntreprises] = useState([]);
  const [b2bUsers, setB2bUsers] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [addingUserFor, setAddingUserFor] = useState(null); // entreprise_id
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserNom, setNewUserNom] = useState("");
  const [lastCreatedCreds, setLastCreatedCreds] = useState(null); // {email, password, ent}
  const [userFilter, setUserFilter] = useState("all"); // all|pending|active|inactive
  const [historyUser, setHistoryUser] = useState(null);
  const [pricingEnt, setPricingEnt] = useState(null);
  const [pricingRows, setPricingRows] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [ficheEnt, setFicheEnt] = useState(null);      // entreprise pour laquelle on affiche la fiche
  const [ficheData, setFicheData] = useState(null);    // {entreprise, stats, breakdown_*, commandes, comptes}
  const [ficheLoading, setFicheLoading] = useState(false);
  const [pendingSummary, setPendingSummary] = useState({ total: 0, by_entreprise: {}, commandes: [] });
  // Boîte de messages B2B
  const [b2bMessages, setB2bMessages] = useState([]);
  const [showMessages, setShowMessages] = useState(false);
  // Partage de liens avec message d'accompagnement personnalisé
  const [shareModal, setShareModal] = useState(null); // { type: 'portail'|'precommande', link, message, email, tel }
  const [templates, setTemplates] = useState({ portail_message: "", precommande_message: "" });

  const fetchAll = async () => {
    try {
      const [ent, u, ps] = await Promise.all([
        axios.get(`${API}/b2b/entreprises`, { headers }),
        axios.get(`${API}/b2b/users`, { headers }),
        axios.get(`${API}/b2b/pending-summary`, { headers }),
      ]);
      setEntreprises(ent.data);
      setB2bUsers(u.data);
      setPendingSummary(ps.data || { total: 0, by_entreprise: {}, commandes: [] });
    } catch { toast.error("Erreur de chargement"); }
  };
  useEffect(() => { fetchAll(); }, []);
  useEffect(() => {
    // Charge les modèles de messages perso pour le partage + précharge les messages B2B pour le badge
    axios.get(`${API}/admin/share-templates`, { headers })
      .then((r) => setTemplates(r.data))
      .catch(() => {});
    axios.get(`${API}/admin/b2b/messages`, { headers })
      .then((r) => {
        setB2bMessages(r.data || []);
        if ((r.data || []).some((m) => !m.traite)) setShowMessages(true);
      })
      .catch(() => {});
  }, []); // eslint-disable-line

  const loadMessages = async () => {
    try {
      const r = await axios.get(`${API}/admin/b2b/messages`, { headers });
      setB2bMessages(r.data || []);
    } catch { toast.error("Erreur chargement messages"); }
  };
  const markMessageTraite = async (id) => {
    try {
      await axios.post(`${API}/admin/b2b/messages/${id}/traite`, {}, { headers });
      setB2bMessages((prev) => prev.map((m) => m.id === id ? { ...m, traite: true } : m));
    } catch { toast.error("Erreur"); }
  };
  const deleteMessage = async (id) => {
    if (!window.confirm("Supprimer ce message ?")) return;
    try {
      await axios.delete(`${API}/admin/b2b/messages/${id}`, { headers });
      setB2bMessages((prev) => prev.filter((m) => m.id !== id));
      toast.success("Message supprimé");
    } catch { toast.error("Erreur"); }
  };

  const openShareModal = (type) => {
    const link = type === "portail"
      ? `${window.location.origin}/b2b-login`
      : `${window.location.origin}/precommande`;
    const tpl = type === "portail" ? templates.portail_message : templates.precommande_message;
    const message = (tpl || "").replace(/\{LIEN\}/g, link);
    setShareModal({ type, link, message, email: "", tel: "" });
  };
  const saveTemplateFromModal = async () => {
    if (!shareModal) return;
    const key = shareModal.type === "portail" ? "portail_message" : "precommande_message";
    // On stocke le modèle avec {LIEN} placeholder — on remet le placeholder si le lien apparait
    const templateToSave = shareModal.message.split(shareModal.link).join("{LIEN}");
    try {
      await axios.put(`${API}/admin/share-templates`, { [key]: templateToSave }, { headers });
      setTemplates((prev) => ({ ...prev, [key]: templateToSave }));
      toast.success("Modèle enregistré");
    } catch { toast.error("Erreur"); }
  };
  const sendShareEmail = () => {
    if (!shareModal) return;
    const subject = shareModal.type === "portail" ? "Ton accès au Portail B2B" : "Notre lien de pré-commande";
    const body = encodeURIComponent(shareModal.message);
    const to = encodeURIComponent(shareModal.email || "");
    window.location.href = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${body}`;
  };
  const sendShareSms = () => {
    if (!shareModal) return;
    const body = encodeURIComponent(shareModal.message);
    const to = (shareModal.tel || "").replace(/\s/g, "");
    // sms: URI — ouvre l'app SMS avec destinataire + corps pré-remplis
    window.location.href = `sms:${to}?body=${body}`;
  };

  const validerCommande = async (livId) => {
    try {
      await axios.post(`${API}/b2b/livraisons/${livId}/valider`, {}, { headers });
      toast.success("Commande acceptée — client B2B notifié par email");
      fetchAll();
      if (ficheEnt) openFiche(ficheEnt);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const refuserCommande = async (livId) => {
    const raison = window.prompt("Raison du refus (envoyée au client B2B) :", "");
    if (raison === null) return;
    try {
      await axios.post(`${API}/b2b/livraisons/${livId}/refuser`, { note: raison }, { headers });
      toast.success("Commande refusée — client B2B notifié");
      fetchAll();
      if (ficheEnt) openFiche(ficheEnt);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const marquerPaye = async (livId) => {
    const mode = window.prompt("Mode de paiement (virement / especes / stripe / autre) :", "virement");
    if (!mode) return;
    const note = window.prompt("Note (optionnel — ex: 'Virement reçu le 24/02, 2500€') :", "") || "";
    try {
      await axios.post(`${API}/b2b/livraisons/${livId}/marquer-paye`, { mode, note }, { headers });
      toast.success("💰 Paiement marqué comme reçu — le B2B peut générer son reçu client");
      if (ficheEnt) openFiche(ficheEnt);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const annulerPaye = async (livId) => {
    if (!window.confirm("Annuler le marquage 'paiement reçu' ?")) return;
    try {
      await axios.post(`${API}/b2b/livraisons/${livId}/annuler-paye`, {}, { headers });
      toast.success("Marquage annulé");
      if (ficheEnt) openFiche(ficheEnt);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const exportFacturesZip = async () => {
    const now = new Date();
    const defYm = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    const ym = window.prompt("Export ZIP des factures TopCocoon → B2B — Mois au format YYYY-MM :", defYm);
    if (!ym || !/^\d{4}-\d{2}$/.test(ym)) return;
    const [y, m] = ym.split("-");
    try {
      const r = await axios.get(`${API}/factures/export-zip?year=${y}&month=${parseInt(m)}`, { headers, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `factures_topcocoon_${y}_${m}.zip`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`ZIP téléchargé — factures ${m}/${y}`);
    } catch (e) {
      toast.error(e?.response?.status === 404 ? "Aucune facture ce mois-ci" : "Erreur");
    }
  };

  // Import KBO Belgique (uniquement si la base est vide — pour la prod)
  const [kboStatus, setKboStatus] = useState(null);
  const [kboUploadStatus, setKboUploadStatus] = useState(null);
  const [kboBusy, setKboBusy] = useState(false);
  useEffect(() => {
    axios.get(`${API}/admin/kbo-import-status`, { headers }).then(r => setKboStatus(r.data)).catch(() => {});
    axios.get(`${API}/admin/kbo-upload-status`, { headers }).then(r => setKboUploadStatus(r.data)).catch(() => {});
  }, []);
  const lancerImportKbo = async () => {
    if (!window.confirm("Lancer l'import de la base KBO Belgique (~2M entreprises) ? Cela prend 2-3 min et remplace la base existante.")) return;
    setKboBusy(true);
    try {
      await axios.post(`${API}/admin/kbo-import`, {}, { headers });
      toast.success("Import lancé — patiente 2-3 min");
      // Poll status
      const t = setInterval(async () => {
        try {
          const r = await axios.get(`${API}/admin/kbo-import-status`, { headers });
          setKboStatus(r.data);
          if (r.data.state === "done" || r.data.state === "error") {
            clearInterval(t);
            setKboBusy(false);
            if (r.data.state === "done") toast.success(`✅ ${r.data.count_in_db.toLocaleString()} entreprises importées`);
            else toast.error(`Erreur : ${r.data.message}`);
          }
        } catch { /* ignore */ }
      }, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
      setKboBusy(false);
    }
  };

  const openFacture = async (livId) => {
    try {
      const r = await axios.get(`${API}/livraisons/${livId}/facture-b2b`, { headers, responseType: "text" });
      const w = window.open("", "_blank");
      w.document.write(r.data);
      w.document.close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nom.trim()) { toast.error("Nom de l'entreprise obligatoire"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/b2b/entreprises`, form, { headers });
      toast.success(`Entreprise créée — code ${res.data.code}`);
      setForm(emptyForm);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  const remove = async (id, nom) => {
    if (!window.confirm(`Supprimer l'entreprise « ${nom} » ?`)) return;
    try {
      await axios.delete(`${API}/b2b/entreprises/${id}`, { headers });
      toast.success("Supprimée");
      fetchAll();
    } catch { toast.error("Erreur suppression"); }
  };

  const toggleActive = async (ent) => {
    try {
      await axios.put(`${API}/b2b/entreprises/${ent.id}`, { active: !ent.active }, { headers });
      fetchAll();
    } catch { toast.error("Erreur"); }
  };

  const copyLink = (code) => {
    // Ancien lien direct (accès sans compte via code)
    const url = `${window.location.origin}/b2b/${code}`;
    navigator.clipboard.writeText(url);
    toast.success("Lien direct (sans login) copié");
  };

  const copyLoginLink = () => {
    const url = `${window.location.origin}/b2b-login`;
    navigator.clipboard.writeText(url);
    toast.success("Lien du portail (avec login) copié");
  };

  const impersonate = async (u) => {
    try {
      const res = await axios.post(`${API}/b2b/users/${u.id}/impersonate`, {}, { headers });
      // Ouvre l'espace B2B dans un nouvel onglet avec la session du client
      localStorage.setItem("b2b_token", res.data.access_token);
      localStorage.setItem("b2b_ent", JSON.stringify(res.data.entreprise));
      localStorage.setItem("b2b_user", JSON.stringify(res.data.user));
      window.open(`${window.location.origin}/b2b-espace`, "_blank");
      toast.success(`Vue « ${u.email} » ouverte dans un nouvel onglet`);
    } catch { toast.error("Erreur"); }
  };

  const openPricing = async (ent) => {
    try {
      let prods = allProducts;
      if (prods.length === 0) {
        const r = await axios.get(`${API}/produits`, { headers });
        prods = r.data;
        setAllProducts(prods);
      }
      const custom = {};
      (ent.prix_custom || []).forEach(pc => { custom[pc.sku] = pc; });
      const rows = prods.map(p => ({
        sku: p.sku,
        modele: p.modele,
        couleur: p.couleur,
        prix_ttc_pro: custom[p.sku]?.prix_ttc_pro || "",
        prix_ttc_conseille: custom[p.sku]?.prix_ttc_conseille || "",
      }));
      setPricingRows(rows);
      setPricingEnt(ent);
    } catch { toast.error("Erreur"); }
  };

  const savePricing = async () => {
    const clean = pricingRows
      .filter(r => r.prix_ttc_pro !== "" && parseFloat(r.prix_ttc_pro) > 0)
      .map(r => ({
        sku: r.sku,
        prix_ttc_pro: parseFloat(r.prix_ttc_pro),
        prix_ttc_conseille: r.prix_ttc_conseille ? parseFloat(r.prix_ttc_conseille) : 0,
      }));
    try {
      await axios.put(`${API}/b2b/entreprises/${pricingEnt.id}`, { prix_custom: clean }, { headers });
      toast.success(`${clean.length} prix custom enregistrés`);
      setPricingEnt(null);
      fetchAll();
    } catch { toast.error("Erreur"); }
  };

  const createB2BUser = async (entId) => {
    if (!newUserEmail.trim()) { toast.error("Email obligatoire"); return; }
    try {
      const res = await axios.post(`${API}/b2b/users`, {
        entreprise_id: entId, email: newUserEmail.trim(), nom: newUserNom.trim(),
      }, { headers });
      const ent = entreprises.find(e => e.id === entId);
      setLastCreatedCreds({
        email: res.data.email,
        password: res.data.generated_password,
        entreprise: ent?.nom || "",
        login_url: `${window.location.origin}/b2b-login`,
      });
      setAddingUserFor(null);
      setNewUserEmail("");
      setNewUserNom("");
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const resetUserPwd = async (userId) => {
    if (!window.confirm("Réinitialiser le mot de passe de cet utilisateur ?")) return;
    try {
      const res = await axios.post(`${API}/b2b/users/${userId}/reset-password`, {}, { headers });
      const u = b2bUsers.find(x => x.id === userId);
      setLastCreatedCreds({
        email: u?.email || "",
        password: res.data.new_password,
        entreprise: entreprises.find(e => e.id === u?.entreprise_id)?.nom || "",
        login_url: `${window.location.origin}/b2b-login`,
        is_reset: true,
      });
    } catch { toast.error("Erreur"); }
  };

  const toggleUserActive = async (userId) => {
    try {
      await axios.put(`${API}/b2b/users/${userId}/toggle-active`, {}, { headers });
      fetchAll();
    } catch { toast.error("Erreur"); }
  };

  const deleteUser = async (userId, email) => {
    if (!window.confirm(`Supprimer l'utilisateur ${email} ?`)) return;
    try {
      await axios.delete(`${API}/b2b/users/${userId}`, { headers });
      toast.success("Utilisateur supprimé");
      fetchAll();
    } catch { toast.error("Erreur"); }
  };

  const copyCreds = () => {
    if (!lastCreatedCreds) return;
    const text = `Portail B2B TopCocoon\n\nEntreprise : ${lastCreatedCreds.entreprise}\nURL : ${lastCreatedCreds.login_url}\nEmail : ${lastCreatedCreds.email}\nMot de passe : ${lastCreatedCreds.password}`;
    navigator.clipboard.writeText(text);
    toast.success("Identifiants copiés");
  };

  const approveUser = async (userId) => {
    try {
      await axios.post(`${API}/b2b/users/${userId}/approve`, {}, { headers });
      toast.success("Compte approuvé — email envoyé au client");
      fetchAll();
    } catch { toast.error("Erreur"); }
  };

  const openFiche = async (ent) => {
    setFicheEnt(ent);
    setFicheData(null);
    setFicheLoading(true);
    try {
      const res = await axios.get(`${API}/b2b/entreprises/${ent.id}/fiche`, { headers });
      setFicheData(res.data);
    } catch { toast.error("Erreur chargement fiche"); setFicheEnt(null); }
    finally { setFicheLoading(false); }
  };

  const filteredUsers = b2bUsers.filter((u) => {
    if (userFilter === "pending") return u.pending_approval;
    if (userFilter === "active") return u.active && !u.pending_approval;
    if (userFilter === "inactive") return !u.active;
    return true;
  });
  const pendingCount = b2bUsers.filter(u => u.pending_approval).length;

  return (
    <div className="space-y-6" data-testid="b2b-admin-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 flex items-center gap-2">
            <Building2 className="w-6 h-6 sm:w-7 sm:h-7 text-indigo-600" /> Entreprises B2B
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            Génère un lien unique par entreprise → accès limité aux produits + commande, sans accès à l&apos;app.
          </p>
        </div>
        <Button variant="outline" onClick={exportFacturesZip} className="text-indigo-600 border-indigo-200" data-testid="b2b-export-zip">
          <FileText className="w-4 h-4 mr-1" /> Export ZIP factures (comptable)
        </Button>
      </div>

      {/* Banner import KBO — visible uniquement si la base est vide (ex: en prod) */}
      {kboStatus && kboStatus.count_in_db < 100000 && (
        <Card className="border-2 border-amber-300 bg-amber-50">
          <CardContent className="pt-4 space-y-3">
            <div>
              <p className="font-semibold text-amber-900 flex items-center gap-2">🇧🇪 Base KBO Belgique — non chargée</p>
              <p className="text-xs text-amber-800 mt-1">Uploade les 4 fichiers CSV depuis ton PC. Les gros fichiers (150+ Mo) sont automatiquement découpés en morceaux de 50 Mo pour éviter la limite serveur.</p>
              {kboStatus.state !== "idle" && (
                <p className="text-xs text-amber-900 mt-1 font-medium">État : {kboStatus.state} — {kboStatus.message} {kboStatus.count > 0 ? `(${kboStatus.count.toLocaleString()} insérées)` : ""}</p>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
              {["enterprise","denomination","address_part1","address_part2"].map((kind) => (
                <label key={kind} className="border-2 border-dashed border-amber-300 rounded p-2 bg-white cursor-pointer hover:bg-amber-100 text-xs">
                  <input type="file" accept=".csv,.zip,.gz" className="hidden" data-testid={`kbo-upload-${kind}`} onChange={async (e) => {
                    const f = e.target.files?.[0]; if (!f) return;
                    const CHUNK = 50 * 1024 * 1024; // 50 Mo (bien sous la limite ingress 100 Mo)
                    const total_parts = Math.max(1, Math.ceil(f.size / CHUNK));
                    toast.info(`Upload ${kind}.csv (${(f.size/1024/1024).toFixed(0)} Mo, ${total_parts} morceau${total_parts>1?'x':''})…`);
                    try {
                      for (let p = 1; p <= total_parts; p++) {
                        const start = (p - 1) * CHUNK;
                        const blob = f.slice(start, Math.min(start + CHUNK, f.size));
                        const fd = new FormData();
                        fd.append("kind", kind);
                        fd.append("part", p);
                        fd.append("total_parts", total_parts);
                        fd.append("file", blob, f.name);
                        const r = await axios.post(`${API}/admin/kbo-upload-file`, fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
                        if (total_parts > 1) toast.success(`✅ ${kind} ${p}/${total_parts} (${r.data.total_size_mb} Mo)`);
                      }
                      toast.success(`✅ ${kind}.csv uploadé complet`);
                      const s = await axios.get(`${API}/admin/kbo-upload-status`, { headers });
                      setKboUploadStatus(s.data);
                    } catch (er) { toast.error(`Erreur ${kind}: ${er?.response?.data?.detail || er.message}`); }
                  }} />
                  <div className="font-semibold">{kboUploadStatus?.files?.[kind]?.present ? "✅" : "📄"} {kind}.csv</div>
                  <div className="text-slate-500">{kboUploadStatus?.files?.[kind]?.present ? `${kboUploadStatus.files[kind].size_mb} Mo` : "Cliquer pour choisir"}</div>
                </label>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <Button
                onClick={lancerImportKbo}
                disabled={kboBusy}
                className="bg-emerald-600 hover:bg-emerald-700 text-white whitespace-nowrap"
                data-testid="kbo-import-btn"
              >
                ⚡ Import auto (depuis URLs)
              </Button>
              <Button
                onClick={async () => {
                  if (!window.confirm("Lancer l'import des 4 fichiers uploadés ? (~2-3 min)")) return;
                  setKboBusy(true);
                  try {
                    await axios.post(`${API}/admin/kbo-import-from-uploads`, {}, { headers });
                    toast.success("Import lancé");
                    const t = setInterval(async () => {
                      const r = await axios.get(`${API}/admin/kbo-import-status`, { headers });
                      setKboStatus(r.data);
                      if (r.data.state === "done" || r.data.state === "error") {
                        clearInterval(t); setKboBusy(false);
                        r.data.state === "done" ? toast.success(`✅ ${r.data.count_in_db.toLocaleString()} entreprises importées`) : toast.error(r.data.message);
                      }
                    }, 3000);
                  } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); setKboBusy(false); }
                }}
                disabled={!kboUploadStatus?.ready || kboBusy}
                className="bg-amber-600 hover:bg-amber-700 text-white whitespace-nowrap disabled:opacity-50"
                data-testid="kbo-import-uploads-btn"
              >
                🚀 {kboBusy ? "En cours…" : "Import (fichiers uploadés)"}
              </Button>
              {["downloading","parsing","inserting","error"].includes(kboStatus.state) && (
                <button className="text-[11px] text-amber-700 underline hover:text-amber-900" onClick={async () => {
                  try {
                    await axios.post(`${API}/admin/kbo-import-reset`, {}, { headers });
                    toast.success("Status réinitialisé");
                    const r = await axios.get(`${API}/admin/kbo-import-status`, { headers });
                    setKboStatus(r.data); setKboBusy(false);
                  } catch { toast.error("Erreur"); }
                }} data-testid="kbo-reset-btn">Réinitialiser le statut</button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bannière : liens du portail à envoyer */}
      <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
        <CardContent className="pt-4 space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-start gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-indigo-800 flex items-center gap-2">
                <LinkIcon className="w-4 h-4" /> Lien Portail B2B (Pros)
              </p>
              <code className="block mt-1 bg-white border border-indigo-100 rounded px-2 py-1.5 text-xs text-indigo-700 truncate" data-testid="b2b-portal-link">
                {`${window.location.origin}/b2b-login`}
              </code>
              <p className="text-xs text-slate-500 mt-1">Auto-inscription : <code>/b2b-register</code> — validation par toi.</p>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <Button onClick={copyLoginLink} variant="outline" className="border-indigo-200 text-indigo-700" data-testid="b2b-copy-portal-link">
                <Copy className="w-4 h-4 mr-1" />Copier
              </Button>
              <Button onClick={() => openShareModal("portail")} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="b2b-share-portal-btn">
                <Send className="w-4 h-4 mr-1" />Envoyer
              </Button>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-start gap-3 border-t border-indigo-100 pt-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-purple-800 flex items-center gap-2">
                <LinkIcon className="w-4 h-4" /> Lien Pré-commande (B2C — anciens clients)
              </p>
              <code className="block mt-1 bg-white border border-purple-100 rounded px-2 py-1.5 text-xs text-purple-700 truncate" data-testid="b2c-precommande-link">
                {`${window.location.origin}/precommande`}
              </code>
              <p className="text-xs text-slate-500 mt-1">Public — produits dispo + produits en précommande avec date d&apos;arrivée.</p>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <Button
                onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/precommande`); toast.success("Lien B2C copié"); }}
                variant="outline"
                className="border-purple-200 text-purple-700"
                data-testid="b2c-copy-link"
              >
                <Copy className="w-4 h-4 mr-1" />Copier
              </Button>
              <Button onClick={() => openShareModal("precommande")} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="b2c-share-btn">
                <Send className="w-4 h-4 mr-1" />Envoyer
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Boîte de messages B2B */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Mail className="w-5 h-5 text-indigo-600" />
              Messages du formulaire Contact B2B
              {b2bMessages.filter(m => !m.traite).length > 0 && (
                <span className="text-xs bg-red-500 text-white rounded-full px-2 py-0.5">{b2bMessages.filter(m => !m.traite).length}</span>
              )}
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { const next = !showMessages; setShowMessages(next); if (next) loadMessages(); }}
              data-testid="b2b-messages-toggle"
            >
              {showMessages ? "Masquer" : "Ouvrir"}
            </Button>
          </div>
        </CardHeader>
        {showMessages && (
          <CardContent>
            {b2bMessages.length === 0 ? (
              <p className="text-xs text-slate-400 italic">Aucun message pour l&apos;instant.</p>
            ) : (
              <div className="space-y-2">
                {b2bMessages.map((m) => (
                  <div key={m.id} className={`border rounded-lg p-3 ${m.traite ? "bg-slate-50 opacity-70" : "bg-white border-indigo-200"}`} data-testid={`b2b-msg-${m.id}`}>
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-sm">{m.nom || "—"}</span>
                          {!m.traite && <span className="text-[10px] bg-red-500 text-white rounded-full px-2 py-0.5">Nouveau</span>}
                          {m.traite && <span className="text-[10px] bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5">Traité</span>}
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">
                          <a href={`mailto:${m.email}`} className="text-indigo-600 hover:underline">{m.email}</a>
                          {m.telephone && <> · <a href={`tel:${m.telephone}`} className="text-indigo-600 hover:underline">{m.telephone}</a></>}
                          {" · "}{new Date(m.date).toLocaleString("fr-BE")}
                        </p>
                        {m.sujet && <p className="text-xs font-medium text-slate-700 mt-1">Sujet : {m.sujet}</p>}
                        <p className="text-sm text-slate-700 whitespace-pre-wrap mt-1.5">{m.message}</p>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        {!m.traite && (
                          <Button size="sm" variant="outline" onClick={() => markMessageTraite(m.id)} data-testid={`b2b-msg-traite-${m.id}`}>
                            <CheckCircle2 className="w-4 h-4" />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => deleteMessage(m.id)} data-testid={`b2b-msg-del-${m.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        )}
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-lg">Nouvelle entreprise B2B</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            <div><Label>Nom de l&apos;entreprise *</Label><Input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} placeholder="ACME SPRL" data-testid="b2b-form-nom" /></div>
            <div><Label>Code (auto si vide)</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="ACME-2026" data-testid="b2b-form-code" /></div>
            <div><Label>Remise B2B (%)</Label><Input type="number" min="0" max="100" step="0.5" value={form.remise_pct} onChange={(e) => setForm({ ...form, remise_pct: parseFloat(e.target.value) || 0 })} data-testid="b2b-form-remise" /></div>
            <div><Label>Contact — Nom</Label><Input value={form.contact_nom} onChange={(e) => setForm({ ...form, contact_nom: e.target.value })} /></div>
            <div><Label>Email</Label><Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></div>
            <div><Label>Téléphone</Label><Input value={form.contact_telephone} onChange={(e) => setForm({ ...form, contact_telephone: e.target.value })} /></div>
            <div className="md:col-span-2"><Label>Adresse</Label><Input value={form.adresse} onChange={(e) => setForm({ ...form, adresse: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>CP</Label><Input value={form.code_postal} onChange={(e) => setForm({ ...form, code_postal: e.target.value })} /></div>
              <div><Label>Ville</Label><Input value={form.ville} onChange={(e) => setForm({ ...form, ville: e.target.value })} /></div>
            </div>
            <div className="md:col-span-3">
              <Button type="submit" disabled={loading} className="bg-indigo-600 hover:bg-indigo-700" data-testid="b2b-form-submit">
                <Plus className="w-4 h-4 mr-2" />{loading ? "Création…" : "Créer entreprise"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-lg">Entreprises B2B ({entreprises.length})</CardTitle></CardHeader>
        <CardContent>
          {entreprises.length === 0 ? (
            <p className="text-slate-400 text-sm">Aucune entreprise B2B pour l&apos;instant.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nom</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Remise</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entreprises.map((e) => (
                    <TableRow key={e.id} data-testid={`b2b-row-${e.id}`}>
                      <TableCell className="font-medium">{e.nom}</TableCell>
                      <TableCell><code className="bg-indigo-50 text-indigo-700 px-2 py-1 rounded text-xs whitespace-nowrap">{e.code}</code></TableCell>
                      <TableCell className="text-xs text-slate-600">
                        {e.contact_nom || "—"}<br/>
                        {e.contact_email && <span className="text-slate-400">{e.contact_email}</span>}
                      </TableCell>
                      <TableCell>{e.remise_pct || 0}%</TableCell>
                      <TableCell>
                        <button onClick={() => toggleActive(e)} className={`text-xs px-2 py-1 rounded-full ${e.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`} data-testid={`b2b-toggle-${e.id}`}>
                          {e.active ? "✓ Actif" : "Désactivé"}
                        </button>
                      </TableCell>
                      <TableCell className="text-right space-x-1 whitespace-nowrap">
                        <Button size="sm" variant="outline" className="text-indigo-600 hover:bg-indigo-50 relative" onClick={() => openFiche(e)} data-testid={`b2b-fiche-${e.id}`}>
                          <FileText className="w-3 h-3 mr-1" />Fiche
                          {pendingSummary.by_entreprise[e.id] > 0 && (
                            <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center animate-pulse" data-testid={`b2b-pending-badge-${e.id}`}>
                              +{pendingSummary.by_entreprise[e.id]}
                            </span>
                          )}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => openPricing(e)} data-testid={`b2b-pricing-${e.id}`}>💰 Prix</Button>
                        <Button size="sm" variant="outline" onClick={() => { setAddingUserFor(e.id); setNewUserEmail(""); setNewUserNom(""); }} data-testid={`b2b-add-user-${e.id}`}><UserPlus className="w-3 h-3 mr-1" />Utilisateur</Button>
                        <Button size="sm" variant="outline" onClick={() => copyLink(e.code)} data-testid={`b2b-copy-${e.id}`}><Copy className="w-3 h-3 mr-1" />Lien</Button>
                        <Button size="sm" variant="outline" onClick={() => window.open(`/b2b/${e.code}`, "_blank")} data-testid={`b2b-open-${e.id}`}><ExternalLink className="w-3 h-3" /></Button>
                        <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => remove(e.id, e.nom)} data-testid={`b2b-delete-${e.id}`}><Trash2 className="w-3 h-3" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <CardTitle className="text-lg">Comptes utilisateurs B2B ({b2bUsers.length})</CardTitle>
            <div className="flex flex-wrap gap-1">
              {[
                { k: "all", label: "Tous" },
                { k: "pending", label: `En attente${pendingCount ? ` (${pendingCount})` : ""}` },
                { k: "active", label: "Actifs" },
                { k: "inactive", label: "Désactivés" },
              ].map(t => (
                <button key={t.k}
                  onClick={() => setUserFilter(t.k)}
                  className={`text-xs px-3 py-1.5 rounded-full font-medium transition ${userFilter === t.k ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                  data-testid={`b2b-filter-${t.k}`}
                >{t.label}{t.k === "pending" && pendingCount > 0 && userFilter !== "pending" ? " 🔴" : ""}</button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredUsers.length === 0 ? (
            <p className="text-slate-400 text-sm">Aucun compte {userFilter !== "all" ? `dans « ${userFilter} »` : ""}.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Entreprise</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Dernière conn.</TableHead>
                    <TableHead>Connexions</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((u) => {
                    const ent = entreprises.find(x => x.id === u.entreprise_id);
                    const isPending = !!u.pending_approval;
                    const status = isPending
                      ? { label: "🟡 En attente", color: "bg-amber-100 text-amber-700" }
                      : u.active
                        ? { label: "✓ Actif", color: "bg-emerald-100 text-emerald-700" }
                        : { label: "⛔ Désactivé", color: "bg-slate-100 text-slate-500" };
                    return (
                      <TableRow key={u.id} data-testid={`b2b-user-row-${u.id}`}>
                        <TableCell className="text-sm">
                          {ent?.nom || u.entreprise_code || "—"}
                          {u.self_registered && <span className="ml-1 text-[10px] text-indigo-500">(auto-inscrit)</span>}
                        </TableCell>
                        <TableCell><code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">{u.email}</code></TableCell>
                        <TableCell>
                          {isPending ? (
                            <span className={`text-xs px-2 py-1 rounded-full ${status.color}`}>{status.label}</span>
                          ) : (
                            <button onClick={() => toggleUserActive(u.id)} className={`text-xs px-2 py-1 rounded-full ${status.color}`} data-testid={`b2b-user-toggle-${u.id}`}>
                              {status.label}
                            </button>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-slate-500 whitespace-nowrap">
                          {u.last_login ? new Date(u.last_login).toLocaleString("fr-BE", { dateStyle: "short", timeStyle: "short" }) : "—"}
                          {u.last_ip && <div className="text-[10px] text-slate-400">IP {u.last_ip}</div>}
                        </TableCell>
                        <TableCell className="text-sm">{u.login_count || 0}</TableCell>
                        <TableCell className="text-right space-x-1 whitespace-nowrap">
                          {isPending && (
                            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => approveUser(u.id)} data-testid={`b2b-user-approve-${u.id}`}>
                              <CheckCircle2 className="w-3 h-3 mr-1" />Approuver
                            </Button>
                          )}
                          <Button size="sm" variant="outline" onClick={() => setHistoryUser(u)} data-testid={`b2b-user-history-${u.id}`}><Eye className="w-3 h-3" /></Button>
                          {!isPending && u.active && (
                            <Button size="sm" variant="outline" className="text-indigo-600 hover:bg-indigo-50" onClick={() => impersonate(u)} data-testid={`b2b-user-impersonate-${u.id}`}>
                              <LogIn className="w-3 h-3 mr-1" />Voir comme
                            </Button>
                          )}
                          <Button size="sm" variant="outline" onClick={() => resetUserPwd(u.id)} data-testid={`b2b-user-reset-${u.id}`}><KeyRound className="w-3 h-3" /></Button>
                          <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => deleteUser(u.id, u.email)} data-testid={`b2b-user-delete-${u.id}`}><Trash2 className="w-3 h-3" /></Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal — Ajouter utilisateur */}
      {addingUserFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setAddingUserFor(null)}>
          <Card className="max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <CardHeader><CardTitle>Créer un utilisateur B2B</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-slate-600">Entreprise : <strong>{entreprises.find(x => x.id === addingUserFor)?.nom}</strong></p>
              <div><Label>Email *</Label><Input type="email" value={newUserEmail} onChange={(e) => setNewUserEmail(e.target.value)} data-testid="b2b-new-user-email" /></div>
              <div><Label>Nom (optionnel)</Label><Input value={newUserNom} onChange={(e) => setNewUserNom(e.target.value)} data-testid="b2b-new-user-nom" /></div>
              <p className="text-xs text-slate-500">Un mot de passe temporaire sera généré et affiché — copie-le pour le transmettre au client.</p>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setAddingUserFor(null)}>Annuler</Button>
                <Button onClick={() => createB2BUser(addingUserFor)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="b2b-new-user-create">Créer</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal — Identifiants générés */}
      {lastCreatedCreds && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setLastCreatedCreds(null)}>
          <Card className="max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <CardHeader><CardTitle>{lastCreatedCreds.is_reset ? "Mot de passe réinitialisé" : "Compte créé"}</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                <p className="font-semibold text-amber-800 mb-2">⚠️ Ce mot de passe ne sera plus visible ensuite. Copie-le maintenant.</p>
                <div className="space-y-1 text-slate-700 font-mono text-xs">
                  <p><strong>URL :</strong> {lastCreatedCreds.login_url}</p>
                  <p><strong>Email :</strong> {lastCreatedCreds.email}</p>
                  <p><strong>Mot de passe :</strong> <span className="bg-white px-2 py-0.5 rounded border select-all">{lastCreatedCreds.password}</span></p>
                  <p><strong>Entreprise :</strong> {lastCreatedCreds.entreprise}</p>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={copyCreds} data-testid="b2b-copy-creds"><Copy className="w-3 h-3 mr-1" />Copier tout</Button>
                <Button onClick={() => setLastCreatedCreds(null)} className="bg-indigo-600 hover:bg-indigo-700">OK</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      {/* Modal — Historique connexions */}
      {historyUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setHistoryUser(null)}>
          <Card className="max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Clock className="w-5 h-5" /> Historique connexions</CardTitle>
            </CardHeader>
            <CardContent className="overflow-y-auto space-y-2">
              <div className="bg-slate-50 rounded p-3 text-sm space-y-1">
                <p><strong>Utilisateur :</strong> {historyUser.email}</p>
                <p><strong>Total connexions :</strong> {historyUser.login_count || 0}</p>
                <p><strong>Dernière IP :</strong> {historyUser.last_ip || "—"}</p>
              </div>
              {(historyUser.connections || []).slice().reverse().map((c, i) => (
                <div key={i} className="border rounded p-2 text-xs" data-testid={`b2b-history-entry-${i}`}>
                  <div className="font-medium">{new Date(c.date).toLocaleString("fr-BE")}</div>
                  <div className="text-slate-500">IP : {c.ip || "?"}</div>
                  <div className="text-slate-400 truncate">{c.user_agent}</div>
                </div>
              ))}
              {(!historyUser.connections || historyUser.connections.length === 0) && (
                <p className="text-slate-400 text-sm text-center py-4">Aucune connexion enregistrée.</p>
              )}
            </CardContent>
            <div className="p-3 border-t flex justify-end">
              <Button onClick={() => setHistoryUser(null)} data-testid="b2b-history-close">Fermer</Button>
            </div>
          </Card>
        </div>
      )}
      {/* Modal — Prix personnalisés par entreprise */}
      {pricingEnt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setPricingEnt(null)}>
          <Card className="max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <CardHeader>
              <CardTitle>Prix personnalisés — {pricingEnt.nom}</CardTitle>
              <p className="text-xs text-slate-500">
                Les prix ici <b>remplacent</b> les prix B2B standards pour cette entreprise uniquement. Laisse vide pour utiliser le prix catalogue (avec remise {pricingEnt.remise_pct || 0}%).
              </p>
            </CardHeader>
            <CardContent className="overflow-y-auto space-y-2">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Produit</TableHead>
                    <TableHead>Prix TTC (pour eux)</TableHead>
                    <TableHead>Prix conseillé TTC (client final)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pricingRows.map((r, i) => (
                    <TableRow key={r.sku}>
                      <TableCell className="text-sm">
                        <div className="font-medium">{r.modele}</div>
                        <div className="text-xs text-slate-500">{r.couleur} · <code>{r.sku}</code></div>
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number" step="0.01" min="0"
                          value={r.prix_ttc_pro}
                          onChange={(e) => { const next = [...pricingRows]; next[i] = { ...r, prix_ttc_pro: e.target.value }; setPricingRows(next); }}
                          className="h-8 w-24"
                          data-testid={`b2b-price-pro-${r.sku}`}
                          placeholder="—"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number" step="0.01" min="0"
                          value={r.prix_ttc_conseille}
                          onChange={(e) => { const next = [...pricingRows]; next[i] = { ...r, prix_ttc_conseille: e.target.value }; setPricingRows(next); }}
                          className="h-8 w-24"
                          data-testid={`b2b-price-conseille-${r.sku}`}
                          placeholder="—"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
            <div className="p-3 border-t flex justify-end gap-2">
              <Button variant="outline" onClick={() => setPricingEnt(null)}>Annuler</Button>
              <Button onClick={savePricing} className="bg-indigo-600 hover:bg-indigo-700" data-testid="b2b-save-pricing">Enregistrer</Button>
            </div>
          </Card>
        </div>
      )}
      {/* Modal — Fiche Société (stats + commandes isolées) */}
      {ficheEnt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setFicheEnt(null)}>
          <Card className="max-w-5xl w-full max-h-[92vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="b2b-fiche-modal">
            <CardHeader className="border-b bg-gradient-to-r from-indigo-50 to-white">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2 text-xl">
                    <Building2 className="w-5 h-5 text-indigo-600" />
                    {ficheEnt.nom}
                    <code className="bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded text-xs">{ficheEnt.code}</code>
                  </CardTitle>
                  <div className="text-xs text-slate-600 mt-1 space-y-0.5">
                    {ficheEnt.contact_nom && <div><strong>Contact :</strong> {ficheEnt.contact_nom}</div>}
                    {ficheEnt.contact_email && <div><strong>Email :</strong> {ficheEnt.contact_email}</div>}
                    {ficheEnt.contact_telephone && <div><strong>Tél :</strong> {ficheEnt.contact_telephone}</div>}
                    {(ficheEnt.adresse || ficheEnt.ville) && <div><strong>Adresse :</strong> {[ficheEnt.adresse, ficheEnt.code_postal, ficheEnt.ville].filter(Boolean).join(", ")}</div>}
                    {ficheEnt.tva && <div><strong>TVA :</strong> {ficheEnt.tva}</div>}
                    <div><strong>Remise B2B :</strong> {ficheEnt.remise_pct || 0}%</div>
                  </div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => setFicheEnt(null)} data-testid="b2b-fiche-close">✕</Button>
              </div>
            </CardHeader>
            <CardContent className="overflow-y-auto space-y-5 py-4">
              {ficheLoading && <div className="text-center py-8 text-slate-400">Chargement…</div>}
              {ficheData && (
                <>
                  {/* KPIs */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="b2b-fiche-stats">
                    <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3">
                      <div className="text-xs text-indigo-600 font-medium">Commandes</div>
                      <div className="text-2xl font-bold text-indigo-900">{ficheData.stats.total_commandes}</div>
                      <div className="text-[10px] text-indigo-500">{ficheData.stats.nb_livrees} livrées · {ficheData.stats.nb_en_cours} en cours</div>
                    </div>
                    <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3">
                      <div className="text-xs text-emerald-600 font-medium">CA total</div>
                      <div className="text-2xl font-bold text-emerald-900">{ficheData.stats.ca_total.toFixed(2)} €</div>
                      <div className="text-[10px] text-emerald-500">Livré : {ficheData.stats.ca_livre.toFixed(2)} €</div>
                    </div>
                    <div className="bg-amber-50 border border-amber-100 rounded-lg p-3">
                      <div className="text-xs text-amber-600 font-medium">CA en attente</div>
                      <div className="text-2xl font-bold text-amber-900">{ficheData.stats.ca_en_attente.toFixed(2)} €</div>
                      <div className="text-[10px] text-amber-500">{ficheData.stats.nb_en_cours} commande(s)</div>
                    </div>
                    <div className="bg-purple-50 border border-purple-100 rounded-lg p-3">
                      <div className="text-xs text-purple-600 font-medium">Panier moyen</div>
                      <div className="text-2xl font-bold text-purple-900">{ficheData.stats.panier_moyen.toFixed(2)} €</div>
                      <div className="text-[10px] text-purple-500">Qté totale : {ficheData.stats.quantite_totale}</div>
                    </div>
                  </div>

                  {/* Breakdowns */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5 mb-2">
                        <Package className="w-4 h-4 text-indigo-600" /> Répartition par couleur
                      </h3>
                      {ficheData.breakdown_couleurs.length === 0 ? (
                        <p className="text-xs text-slate-400 italic">Aucune donnée.</p>
                      ) : (
                        <div className="space-y-1.5" data-testid="b2b-fiche-couleurs">
                          {ficheData.breakdown_couleurs.map((c) => {
                            const pct = ficheData.stats.quantite_totale ? (c.quantite / ficheData.stats.quantite_totale * 100) : 0;
                            return (
                              <div key={c.couleur} className="text-xs">
                                <div className="flex justify-between mb-0.5">
                                  <span className="font-medium text-slate-700">{c.couleur}</span>
                                  <span className="text-slate-500">{c.quantite} unité{c.quantite > 1 ? "s" : ""} · {pct.toFixed(0)}%</span>
                                </div>
                                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                  <div className="h-full bg-indigo-500" style={{ width: `${pct}%` }} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5 mb-2">
                        <TrendingUp className="w-4 h-4 text-emerald-600" /> Top produits (modèle + couleur)
                      </h3>
                      {ficheData.breakdown_produits.length === 0 ? (
                        <p className="text-xs text-slate-400 italic">Aucun produit commandé.</p>
                      ) : (
                        <div className="space-y-1" data-testid="b2b-fiche-produits">
                          {ficheData.breakdown_produits.slice(0, 8).map((p) => (
                            <div key={`${p.modele}-${p.couleur}`} className="flex justify-between text-xs border-b border-slate-100 py-1">
                              <span className="text-slate-700"><strong>{p.modele}</strong> {p.couleur && <span className="text-slate-400">· {p.couleur}</span>}</span>
                              <span className="text-slate-500 whitespace-nowrap">{p.quantite} pcs · {p.ca.toFixed(0)} €</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Commandes en attente de validation (P0 — actions Valider/Refuser) */}
                  {ficheData.commandes.some(c => c.statut === "en_attente_validation") && (
                    <div className="border-2 border-amber-300 bg-amber-50 rounded-lg p-3" data-testid="b2b-fiche-pending-section">
                      <h3 className="text-sm font-semibold text-amber-800 mb-2 flex items-center gap-1.5">
                        <Clock className="w-4 h-4" /> Commandes en attente de validation ({ficheData.commandes.filter(c => c.statut === "en_attente_validation").length})
                      </h3>
                      <div className="space-y-2">
                        {ficheData.commandes.filter(c => c.statut === "en_attente_validation").map((c) => (
                          <div key={c.id} className="bg-white border border-amber-200 rounded-lg p-3" data-testid={`b2b-pending-cmd-${c.id}`}>
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0 flex-1">
                                <p className="font-semibold text-sm">#{c.numero_commande} · <span className={`text-[10px] px-2 py-0.5 rounded-full ${c.livrer_chez === "client_final" ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"}`}>
                                  {c.livrer_chez === "client_final" ? "🏠 Client final" : "📦 Chez B2B"}
                                </span></p>
                                <p className="text-xs text-slate-600 mt-1">{c.client_prenom} {c.client_nom} · {c.client_telephone}</p>
                                <p className="text-xs text-slate-500">{c.adresse}, {c.code_postal} {c.ville}</p>
                                <p className="text-xs text-slate-500 mt-1">{c.details_commande}</p>
                                <div className="flex flex-wrap gap-2 mt-1 text-[10px]">
                                  <span className="bg-slate-100 rounded px-1.5 py-0.5">Total : {((c.prix || 0) + (c.frais_livraison || 0)).toFixed(2)} €</span>
                                  <span className="bg-slate-100 text-slate-700 rounded px-1.5 py-0.5">Dû par B2B : {(c.paye_par_b2b || 0).toFixed(2)} €</span>
                                  {c.a_encaisser > 0 && <span className="bg-amber-100 text-amber-700 rounded px-1.5 py-0.5">À encaisser client : {c.a_encaisser.toFixed(2)} €</span>}
                                  {c.mode_paiement_souhaite === "especes" && <span className="bg-amber-100 text-amber-800 rounded px-1.5 py-0.5">💵 Cash souhaité</span>}
                                  {c.mode_paiement_souhaite === "stripe" && <span className="bg-blue-100 text-blue-800 rounded px-1.5 py-0.5">💳 Stripe souhaité</span>}
                                  {(c.mode_paiement_souhaite === "virement" || !c.mode_paiement_souhaite) && <span className="bg-indigo-100 text-indigo-800 rounded px-1.5 py-0.5">🏦 Virement souhaité</span>}
                                  {c.date_livraison_prevue && <span className="bg-slate-100 rounded px-1.5 py-0.5">Souhaitée : {c.date_livraison_prevue}</span>}
                                </div>
                              </div>
                              <div className="flex flex-col gap-1">
                                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => validerCommande(c.id)} data-testid={`b2b-valider-${c.id}`}>
                                  <CheckCircle2 className="w-3 h-3 mr-1" />Accepter
                                </Button>
                                <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => refuserCommande(c.id)} data-testid={`b2b-refuser-${c.id}`}>
                                  <Trash2 className="w-3 h-3 mr-1" />Refuser
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Clients ramenés par cette société B2B */}
                  {(ficheData.clients_ramenes || []).length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
                        <Building2 className="w-4 h-4 text-indigo-600" /> Clients ramenés par {ficheEnt.nom} ({ficheData.clients_ramenes.length})
                      </h3>
                      <div className="border rounded-lg overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-slate-50">
                              <TableHead className="text-xs">Nom</TableHead>
                              <TableHead className="text-xs">Contact</TableHead>
                              <TableHead className="text-xs">Adresse</TableHead>
                              <TableHead className="text-xs">Commandes</TableHead>
                              <TableHead className="text-xs">Dernière</TableHead>
                              <TableHead className="text-xs w-10"></TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {ficheData.clients_ramenes.map((c) => (
                              <TableRow key={c.id} data-testid={`b2b-client-ramene-${c.id}`}>
                                <TableCell className="text-xs font-medium">{c.prenom} {c.nom}</TableCell>
                                <TableCell className="text-xs text-slate-500">{c.telephone}<br/>{c.email}</TableCell>
                                <TableCell className="text-xs text-slate-500">{c.adresse}, {c.code_postal} {c.ville}</TableCell>
                                <TableCell className="text-xs">{c.nombre_commandes || 1}</TableCell>
                                <TableCell className="text-xs whitespace-nowrap">{c.derniere_commande ? new Date(c.derniere_commande).toLocaleDateString("fr-BE") : "—"}</TableCell>
                                <TableCell className="text-xs">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0"
                                    data-testid={`b2b-fiche-delete-client-${c.id}`}
                                    onClick={async () => {
                                      if (!window.confirm(`Supprimer ${c.prenom || ""} ${c.nom} (client ramené par ${ficheEnt.nom}) ?\n\nAction définitive.`)) return;
                                      try {
                                        await axios.delete(`${API}/b2b/entreprises/${ficheEnt.id}/clients/${c.id}`, { headers });
                                        toast.success("Client supprimé");
                                        setFicheData((prev) => prev ? { ...prev, clients_ramenes: prev.clients_ramenes.filter((x) => x.id !== c.id) } : prev);
                                      } catch (e) {
                                        toast.error(e?.response?.data?.detail || "Erreur");
                                      }
                                    }}
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}

                  {/* Commandes isolées */}
                  <div>
                    <h3 className="text-sm font-semibold text-slate-700 mb-2">Commandes de cette entreprise ({ficheData.commandes.length})</h3>
                    {ficheData.commandes.length === 0 ? (
                      <p className="text-xs text-slate-400 italic">Aucune commande pour l&apos;instant.</p>
                    ) : (
                      <div className="border rounded-lg overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-slate-50">
                              <TableHead className="text-xs">N°</TableHead>
                              <TableHead className="text-xs">Date</TableHead>
                              <TableHead className="text-xs">Détails</TableHead>
                              <TableHead className="text-xs">Qté</TableHead>
                              <TableHead className="text-xs">Total</TableHead>
                              <TableHead className="text-xs">Statut</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {ficheData.commandes.map((c) => {
                              const st = (c.statut || "").toLowerCase();
                              const color = st.startsWith("livr") ? "bg-emerald-100 text-emerald-700"
                                : st.startsWith("annul") ? "bg-red-100 text-red-600"
                                : st.startsWith("en_attente") ? "bg-amber-100 text-amber-700"
                                : "bg-slate-100 text-slate-600";
                              return (
                                <TableRow key={c.id} data-testid={`b2b-fiche-cmd-${c.numero_commande || c.id}`}>
                                  <TableCell className="text-xs font-mono">
                                    #{c.numero_commande || "—"}
                                    <div className="mt-0.5">
                                      <Button size="sm" variant="ghost" className="h-5 text-[9px] py-0 px-1 text-slate-500 hover:text-indigo-600" onClick={() => openFacture(c.id)} data-testid={`b2b-facture-${c.id}`} title="Générer facture TopCocoon → B2B">
                                        📄 Facture
                                      </Button>
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-xs whitespace-nowrap">{c.date_creation ? new Date(c.date_creation).toLocaleDateString("fr-BE") : "—"}</TableCell>
                                  <TableCell className="text-xs max-w-xs truncate">{c.details_commande || "—"}</TableCell>
                                  <TableCell className="text-xs">{c.quantite || 0}</TableCell>
                                  <TableCell className="text-xs whitespace-nowrap">
                                    {((c.prix || 0) + (c.frais_livraison || 0)).toFixed(2)} €
                                    {c.statut_paiement === "paye" && c.paiement_recu_mode === "stripe" && (
                                      <div className="text-[10px] px-1 py-0.5 mt-0.5 rounded bg-emerald-100 text-emerald-700 inline-block">✅ Payé Stripe</div>
                                    )}
                                    {c.statut_paiement === "paye" && c.paiement_recu_mode !== "stripe" && (
                                      <div className="text-[10px] px-1 py-0.5 mt-0.5 rounded bg-emerald-100 text-emerald-700 inline-block">
                                        ✅ Payé ({c.paiement_recu_mode === "especes" ? "cash" : (c.paiement_recu_mode || "virement")})
                                      </div>
                                    )}
                                    {/* Attente paiement — affiche le mode souhaité par le B2B */}
                                    {c.statut_paiement !== "paye" && !c.paiement_recu_admin && (
                                      <>
                                        {c.mode_paiement_souhaite === "especes" && (
                                          <div className="text-[10px] px-1 py-0.5 mt-0.5 rounded bg-amber-100 text-amber-800 inline-block">💵 Cash à la livraison</div>
                                        )}
                                        {c.mode_paiement_souhaite === "stripe" && (
                                          <div className="text-[10px] px-1 py-0.5 mt-0.5 rounded bg-blue-100 text-blue-800 inline-block">💳 Attente paiement Stripe</div>
                                        )}
                                        {(c.mode_paiement_souhaite === "virement" || !c.mode_paiement_souhaite) && (
                                          <div className="text-[10px] px-1 py-0.5 mt-0.5 rounded bg-slate-100 text-slate-700 inline-block">🏦 Virement en attente</div>
                                        )}
                                      </>
                                    )}
                                    {!c.paiement_recu_admin && c.statut_paiement !== "paye" && ["en_attente", "en_cours", "livree"].includes(c.statut) && (
                                      <div className="mt-1">
                                        <Button size="sm" variant="outline" className="h-6 text-[10px] py-0 px-1.5 text-emerald-600" onClick={() => marquerPaye(c.id)} data-testid={`b2b-marquer-paye-${c.id}`}>
                                          ✔ Marquer comme payé
                                        </Button>
                                      </div>
                                    )}
                                    {c.paiement_recu_admin && (
                                      <div className="mt-0.5">
                                        <button className="text-[9px] text-slate-400 hover:text-red-500 underline" onClick={() => annulerPaye(c.id)} data-testid={`b2b-annuler-paye-${c.id}`}>Annuler paiement</button>
                                      </div>
                                    )}
                                  </TableCell>
                                  <TableCell><span className={`text-[10px] px-2 py-0.5 rounded-full ${color}`}>{c.statut || "—"}</span></TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                </>
              )}
            </CardContent>
            <div className="p-3 border-t flex justify-end">
              <Button onClick={() => setFicheEnt(null)} data-testid="b2b-fiche-close-btn">Fermer</Button>
            </div>
          </Card>
        </div>
      )}
      {/* Modal partage lien (précommande / portail) avec message perso */}
      {shareModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setShareModal(null)}>
          <Card className="w-full max-w-xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Send className={`w-5 h-5 ${shareModal.type === "portail" ? "text-indigo-600" : "text-purple-600"}`} />
                Envoyer le lien {shareModal.type === "portail" ? "Portail B2B" : "Pré-commande"}
              </CardTitle>
              <p className="text-xs text-slate-500">Personnalise le message ci-dessous puis choisis Email ou SMS.</p>
            </CardHeader>
            <CardContent className="space-y-3 overflow-y-auto">
              <div>
                <Label className="text-xs">Lien</Label>
                <code className={`block mt-1 rounded px-2 py-1.5 text-xs truncate ${shareModal.type === "portail" ? "bg-indigo-50 text-indigo-700" : "bg-purple-50 text-purple-700"}`}>
                  {shareModal.link}
                </code>
              </div>
              <div>
                <Label className="text-xs">Message d&apos;accompagnement</Label>
                <textarea
                  className="w-full min-h-[140px] mt-1 border rounded p-2 text-sm font-mono"
                  value={shareModal.message}
                  onChange={(e) => setShareModal({ ...shareModal, message: e.target.value })}
                  data-testid="share-modal-message"
                />
                <div className="flex justify-between items-center mt-1">
                  <p className="text-[10px] text-slate-400">Astuce : le lien est déjà inséré. Modifie le texte à ta convenance.</p>
                  <Button size="sm" variant="ghost" className="text-xs text-slate-600" onClick={saveTemplateFromModal} data-testid="share-save-template">
                    Enregistrer comme modèle par défaut
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t">
                <div>
                  <Label className="text-xs">Email destinataire</Label>
                  <Input
                    type="email"
                    placeholder="client@exemple.com"
                    value={shareModal.email}
                    onChange={(e) => setShareModal({ ...shareModal, email: e.target.value })}
                    data-testid="share-modal-email"
                  />
                  <Button className="w-full mt-2 bg-blue-600 hover:bg-blue-700 text-white" onClick={sendShareEmail} data-testid="share-send-email">
                    <Mail className="w-4 h-4 mr-2" />Envoyer par Email
                  </Button>
                </div>
                <div>
                  <Label className="text-xs">Numéro téléphone (SMS)</Label>
                  <Input
                    type="tel"
                    placeholder="+32 470 12 34 56"
                    value={shareModal.tel}
                    onChange={(e) => setShareModal({ ...shareModal, tel: e.target.value })}
                    data-testid="share-modal-tel"
                  />
                  <Button className="w-full mt-2 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={sendShareSms} data-testid="share-send-sms">
                    <Send className="w-4 h-4 mr-2" />Envoyer par SMS
                  </Button>
                </div>
              </div>
              <p className="text-[10px] text-slate-400 italic pt-2">
                Email : ouvre ton app mail avec le message pré-rempli. SMS : ouvre l&apos;app SMS de ton téléphone.
              </p>
            </CardContent>
            <div className="p-3 border-t flex justify-end">
              <Button variant="outline" onClick={() => setShareModal(null)} data-testid="share-modal-close">Fermer</Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default B2BAdmin;
